// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Lock, ShieldAlert, Globe } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getApiCall } from '@/service/getApiCall';
import { postApiCall } from '@/service/postApiCall';
import { API_COMPANY_MY_ASSETS_VISIBILITY } from '@/service/url';
import { friendlyApiError } from '@/utilities/apiErrorMessage';

const OTHER = (visibility) => (visibility === 'public' ? 'private' : 'public');

const VISIBILITY_COPY = {
  private: {
    icon: Lock,
    title: 'Private',
    blurb: 'Only signed-in users who are allowed to see this company can open these files.',
  },
  public: {
    icon: Globe,
    title: 'Public',
    blurb: 'Anyone who has a file’s link can open it, without signing in.',
  },
};

export default function AssetsVisibilityModal({ open, onOpenChange, company }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setPending(null);
    try {
      const resp = await getApiCall(API_COMPANY_MY_ASSETS_VISIBILITY);
      const assetTypes = resp?.asset_types || [];
      setItems(assetTypes.filter((item) => item.can_change !== false));
    } catch {
      // getApiCall raises its own toast, so this only has to clear the list.
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const confirmChange = async () => {
    if (!pending || saving) return;
    setSaving(true);
    try {
      const resp = await postApiCall(API_COMPANY_MY_ASSETS_VISIBILITY, {
        asset_type: pending.asset_type,
        assets_visibility: pending.next,
      });
      const next = resp?.assets_visibility || pending.next;

      toast.success(
        resp?.changed === false
          ? `${pending.label} was already ${next}`
          : resp?.pending
            ? `${pending.label} are now ${next}`
            : `${pending.label} are now ${next}`,
      );
      setPending(null);
      await load();
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Could not change assets visibility');
    } finally {
      setSaving(false);
    }
  };

  const renderList = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking current visibility…
        </div>
      );
    }

    if (!items.length) {
      return (
        <p className="py-8 text-center text-sm text-muted-foreground">
          There is nothing you can change here for this company.
        </p>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        <p className="text-xs text-muted-foreground">
          Changes can take up to 30 seconds to take effect. Until then this may
          still show the previous setting.
        </p>
        {items.map((item) => {
          const copy = VISIBILITY_COPY[item.visibility] || VISIBILITY_COPY.private;
          const Icon = copy.icon;
          const disabled = item.readable === false;

          return (
            <div key={item.asset_type} className="rounded-md border bg-card p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Icon className="h-4 w-4 shrink-0" />
                    {item.label}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Currently <strong>{copy.title.toLowerCase()}</strong>.
                  </p>
                  {item.readable === false && (
                    <p className="mt-2 text-xs text-destructive">
                      Storage could not be reached, so this cannot be changed right now.
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  onClick={() =>
                    setPending({
                      asset_type: item.asset_type,
                      label: item.label,
                      current: item.visibility,
                      next: OTHER(item.visibility),
                    })
                  }
                >
                  Make {OTHER(item.visibility)}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderConfirm = () => (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border bg-card p-4 text-sm">
        <p>
          <strong>{pending.label}</strong> for {company?.name || 'this company'} is
          currently <strong>{pending.current}</strong>.
        </p>
        <p className="mt-2">
          Do you want to change it to <strong>{pending.next}</strong>?
        </p>
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setPending(null);
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            Assets Visibility
          </DialogTitle>
          <DialogDescription>
            {pending
              ? 'Confirm this change.'
              : `Control who can open stored files for ${company?.name || 'this company'}.`}
          </DialogDescription>
        </DialogHeader>

        {pending ? renderConfirm() : renderList()}

        <DialogFooter>
          {pending ? (
            <>
              <Button variant="outline" onClick={() => setPending(null)} disabled={saving}>
                No, keep it {pending.current}
              </Button>
              <Button
                variant={pending.next === 'public' ? 'destructive' : 'default'}
                onClick={confirmChange}
                disabled={saving}
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Yes, make it {pending.next}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
